import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { OUTSTANDING_PAGE_SIZE, outstandingQuery } from '@/api/outstanding';
import { useSession } from '@/app/session';
import { ApiErrorState } from '@/components/states/ApiErrorState';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { type } from '@/theme/typography';

import { useOutstandingColumns } from './outstandingRow';
import { outstandingUrl, readOutstandingPage } from './outstandingUrls';

/**
 * `DESIGN.md` §6.2a — every outstanding interview this viewer may see, which is where §6.2 Section 1's
 * *View all →* goes (TASK-157).
 *
 * **The defect it closes is measured, not stylistic.** Section 1 renders five rows while stating a
 * total that is often larger — eight on the dev tenant — so the rest of the pile was unreachable from
 * the one screen whose subject is outstanding interviews.
 *
 * ⚠️ **No `RequireGroup`, and that is the permission matrix's own answer rather than an omission** —
 * the argument `/candidates` and the interview address already carry in `routes.tsx`. All three groups
 * get `200` from `GET /api/v1/dashboard/outstanding/`, so a gate over all three could never refuse.
 * What differs per caller is the **queryset**: an `Interviewer` is answered their own outstanding work
 * and a `Recruiter`/`Admin` the tenant's, decided by the server through the same `sees_every_candidate`
 * predicate the candidate surface reads. The screen renders what it is given and branches on no group.
 *
 * ⚠️ **Nothing outstanding is a *good* state, and it is the common one here.** For an `Interviewer` this
 * screen is their to-do list, so its usual answer is that they are up to date — which is §6.2's own
 * sentence in `positive`, never an `EmptyState` card, whose shape is the shape of a problem (§8).
 *
 * **No draft content anywhere on it** (ADR 0006): the row is a candidate, a vacancy, an interviewer, an
 * age and a link, and the API sends nothing else.
 */
export default function Outstanding() {
  const { t } = useTranslation('outstanding');
  const session = useSession();

  return (
    <>
      {/* §9: one `h1`, the screen's own, in every one of §8's states — including the failed one. */}
      <h1 className={type.pageTitle}>{t('title')}</h1>

      {/*
       * ⚠️ **No viewer, no query key.** A disabled query still reads the cache at its key, so a
       * placeholder id is one shared bucket every session-less render looks into — and this payload is
       * narrowed per caller (`queryKeys.outstanding`). The component that builds the key does not exist
       * until there is a viewer; until then this is §8's loading state, which is what it would render
       * anyway.
       */}
      {session.data ? <OutstandingList viewerId={session.data.user.id} /> : <LoadingRows />}
    </>
  );
}

/** §8's loading state: rows shaped like rows, never a centred spinner. `Table` owns the shape. */
function LoadingRows() {
  return (
    <div className="mt-6">
      <Table columns={[]} rows={[]} rowKey={() => 0} isLoading />
    </div>
  );
}

function OutstandingList({ viewerId }: { viewerId: number }) {
  const { t } = useTranslation(['outstanding', 'dashboard']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const columns = useOutstandingColumns();

  /** The address bar is the page's source of truth (`outstandingUrls.ts`), so a page is linkable. */
  const page = readOutstandingPage(searchParams);
  const list = useQuery(outstandingQuery(viewerId, page));

  const goToPage = (next: number) =>
    // A page is a new address, not a correction of the previous one: Back goes back a page.
    void navigate(outstandingUrl(next), { replace: false });

  if (list.isError) {
    /**
     * One mapping for every failure (`ApiErrorState`): offline, throttled, or this screen's sentence
     * with a retry. A 404 here would be a **collection** route answering, which means a broken
     * deployment and reads like one — the "outside your reach" mapping is scoped to record routes.
     */
    return (
      <div className="mt-10">
        <ApiErrorState
          error={list.error}
          message={t('outstanding:error')}
          onRetry={() => void list.refetch()}
        />
      </div>
    );
  }

  if (list.isPending) return <LoadingRows />;

  const rows = list.data.items;
  const count = list.data.count;
  const first = rows.length === 0 ? 0 : (page - 1) * OUTSTANDING_PAGE_SIZE + 1;
  const last = rows.length === 0 ? 0 : first + rows.length - 1;

  /*
   * ⚠️ **`count === 0` is the good state and `rows.length === 0` on page 7 is not.** They are two
   * different sentences (§8): nothing is outstanding at all, versus a page past the end of a list that
   * does have work on it. The second keeps the pagination on screen so the reader can walk back — a
   * clamp would rewrite their address for them, which is the shape §6.3's *Next* button got wrong.
   */
  if (count === 0) {
    return (
      <p className="mt-6 text-sm text-positive">
        {/*
         * §6.2's own zero-state sentence, from §6.2's own catalogue — one string, not a second copy
         * of it. A reader who follows *View all →* from a section that says this must not be told the
         * same thing in different words.
         */}
        {t('dashboard:nothingOutstanding')}
      </p>
    );
  }

  return (
    <>
      {/*
       * ⚠️ **A page past the end renders its own sentence rather than `Table`'s empty state.** `Table`
       * answers no rows with an `EmptyState` card — the shape of a problem (§8) — and an address the
       * reader typed past the end of a real list is not one. The count and the pagination stay on
       * screen so there is a way back, which is what makes this a state rather than a dead end.
       */}
      {rows.length === 0 ? (
        <p className={`${type.metadata} mt-6`}>{t('outstanding:pastTheEnd')}</p>
      ) : (
        <div className="mt-6">
          <Table columns={columns} rows={rows} rowKey={(row) => row.interview_id} />
        </div>
      )}

      {/*
       * §6.3's decided pagination shape, reused rather than re-invented: the visible range with the
       * count, Previous/Next, and **no page-number strip**.
       */}
      <nav
        className="mt-4 flex flex-wrap items-center justify-between gap-3"
        aria-label={t('outstanding:pagination.label')}
      >
        <p className={type.metadata}>{t('outstanding:pagination.range', { first, last, count })}</p>
        <span className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
          >
            {t('outstanding:pagination.previous')}
          </Button>
          {/*
           * ⚠️ `rows.length === 0` is not redundant with `last >= count` — §6.3's own sprint-end
           * finding. On an empty page `first` and `last` are both `0`, so `0 >= 137` is false and
           * *Next* stayed live on `?page=99`, walking to page 100 and every page after it. An empty
           * page has no next page whatever the total says.
           */}
          <Button
            variant="secondary"
            size="sm"
            disabled={rows.length === 0 || last >= count}
            onClick={() => goToPage(page + 1)}
          >
            {t('outstanding:pagination.next')}
          </Button>
        </span>
      </nav>
    </>
  );
}
